import { Inject } from '@angular/core';
import templateSource from './view.html';
              import { Component } from '@angular/core';

import Wiz from 'src/wiz';
let wiz = new Wiz('/wiz').app('component.market.chart');
import { Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { Service } from "src/libs/portal/season/service";
import {
    buildChartModel,
    chartBarLimit,
    ChartLevel,
    ChartModel,
    MarketBar,
    PriceTick
} from './chart-model';
import {
    ChartSelection,
    selectionAtIndex,
    selectionByKey
} from './chart-interaction';
import {
    formatBarTime,
    formatNumber,
    formatPercent,
    formatPrice,
    formatTime,
    formatVolume
} from './chart-format';
import {
    chartAriaValueText,
    chartSummary,
    displayChangePercentage,
    emptyChartText,
    InteractionMode,
    pointerContext,
    TooltipPosition
} from './chart-view-state';

@Component({
    selector: 'wiz-component-market-chart',
template: templateSource || '',
    styles: [`

/* file: /mnt/data/wiz/project/main/build/src/app/component.market.chart/view.scss */
@charset "UTF-8";
:host {
  display: block;
  min-width: 0;
}

.hn-chart-shell {
  container-type: inline-size;
  padding: 10px 14px 12px;
}

.hn-chart-toolbar {
  align-items: center;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  display: flex;
  gap: 14px;
  justify-content: space-between;
  min-height: 40px;
  padding: 0 4px 9px;
}

.hn-chart-context {
  align-items: center;
  display: flex;
  flex: 0 0 auto;
  flex-wrap: wrap;
  gap: 9px;
}

.hn-chart-context strong {
  color: var(--hn-accent);
  font-size: 13px;
  font-weight: 800;
}

.hn-chart-context span,
.hn-chart-context time {
  color: rgba(255, 255, 255, 0.72);
  font-size: 13px;
}

.hn-chart-context .hn-chart-date-warning {
  color: #ffd477;
  font-weight: 700;
}

.hn-chart-context .hn-chart-cycle-mode {
  background: rgba(169, 201, 92, 0.12);
  border-radius: 999px;
  color: #cce58f;
  font-weight: 700;
  padding: 3px 7px;
}

.hn-chart-context .hn-chart-change {
  font-variant-numeric: tabular-nums;
  font-weight: 700;
}

.hn-chart-context .hn-chart-change.is-up {
  color: #ff948c;
}

.hn-chart-context .hn-chart-change.is-down {
  color: #78abe8;
}

.hn-chart-latest {
  background: transparent;
  border: 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.52);
  color: rgba(255, 255, 255, 0.86);
  cursor: pointer;
  font: inherit;
  font-size: 13px;
  font-weight: 700;
  min-height: 40px;
  min-width: 44px;
  padding: 0 8px;
}

.hn-chart-latest:focus-visible {
  outline: 2px solid var(--hn-accent);
  outline-offset: 3px;
}

.hn-chart-context time::before {
  color: rgba(255, 255, 255, 0.25);
  content: "·";
  margin-right: 9px;
}

.hn-chart-readout {
  align-items: center;
  display: flex;
  gap: 16px;
  margin: 0;
}

.hn-chart-readout > div {
  align-items: baseline;
  display: flex;
  gap: 5px;
  min-width: 0;
}

.hn-chart-readout dt {
  color: rgba(255, 255, 255, 0.68);
  font-size: 13px;
}

.hn-chart-readout dd {
  color: rgba(255, 255, 255, 0.9);
  font-size: 14px;
  font-variant-numeric: tabular-nums;
  font-weight: 700;
  margin: 0;
  white-space: nowrap;
}

.hn-chart-plot {
  --hn-chart-axis-space: 76px;
  height: 352px;
  margin-top: 7px;
  padding-right: 76px;
  position: relative;
}

.hn-price-chart {
  display: block;
  height: 330px;
  overflow: visible;
  width: 100%;
}

.hn-price-chart.is-interactive {
  cursor: default;
  touch-action: pan-y;
}

.hn-price-chart:focus {
  outline: none;
}

.hn-price-chart:focus-visible:not(.is-pointer-focus) {
  outline: 2px solid var(--hn-accent);
  outline-offset: 2px;
}

.hn-chart-grid line {
  stroke: rgba(255, 255, 255, 0.08);
  stroke-dasharray: 2 5;
  stroke-width: 1;
  vector-effect: non-scaling-stroke;
}

.hn-chart-grid line.is-vertical {
  stroke: rgba(255, 255, 255, 0.045);
}

.hn-chart-grid .hn-volume-separator {
  stroke: rgba(255, 255, 255, 0.11);
  stroke-dasharray: none;
}

.hn-chart-grid .hn-volume-baseline {
  stroke: rgba(255, 255, 255, 0.14);
  stroke-dasharray: none;
}

.hn-candle-wick {
  stroke-width: 1.6;
  vector-effect: non-scaling-stroke;
}

.hn-candle-wick.is-up {
  stroke: #ff948c;
}

.hn-candle-wick.is-down {
  stroke: #78abe8;
}

.hn-candle-body.is-up {
  fill: #f7746c;
}

.hn-candle-body.is-down {
  fill: #5791d7;
}

.hn-volume-bar {
  opacity: 0.58;
}

.hn-volume-bar.is-up {
  fill: #f7746c;
}

.hn-volume-bar.is-down {
  fill: #5791d7;
}

.hn-volume-bar.is-capped {
  opacity: 0.86;
  stroke: rgba(255, 255, 255, 0.52);
  stroke-width: 0.7;
  vector-effect: non-scaling-stroke;
}

.hn-chart-candle .hn-candle-wick,
.hn-chart-candle .hn-candle-body,
.hn-chart-candle .hn-volume-bar {
  transition: opacity 0.12s ease, stroke-width 0.12s ease;
}

.hn-chart-candle.is-selected .hn-candle-wick {
  filter: drop-shadow(0 0 2px rgba(255, 255, 255, 0.46));
  stroke: #fff;
  stroke-width: 2.3;
}

.hn-chart-candle.is-selected .hn-candle-body {
  filter: drop-shadow(0 0 2px rgba(255, 255, 255, 0.38));
  stroke: rgba(255, 255, 255, 0.96);
  stroke-width: 1.1;
  vector-effect: non-scaling-stroke;
}

.hn-chart-candle.is-selected .hn-volume-bar {
  opacity: 1;
}

.hn-level-line,
.hn-current-price-line {
  stroke: var(--hn-accent);
  stroke-dasharray: 6 5;
  stroke-width: 1.1;
  vector-effect: non-scaling-stroke;
}

.hn-level-line.is-reference {
  stroke: rgba(255, 255, 255, 0.48);
  stroke-dasharray: 2 5;
}

.hn-level-line.is-entry {
  stroke: #79aef4;
  stroke-dasharray: 6 4;
}

.hn-level-line.is-entry-high {
  stroke: #c4a0ff;
  stroke-dasharray: 2 4;
}

.hn-level-line.is-initial {
  stroke: rgba(255, 255, 255, 0.92);
  stroke-dasharray: none;
}

.hn-level-line.is-add {
  stroke: #79aef4;
  stroke-dasharray: 5 4;
}

.hn-level-line.is-average {
  stroke: #ffd477;
  stroke-dasharray: 3 4;
}

.hn-level-line.is-target-2 {
  stroke: #c7de89;
}

.hn-level-line.is-target-3 {
  stroke: #e1edc1;
}

.hn-level-line.is-order {
  stroke: rgba(255, 255, 255, 0.88);
  stroke-dasharray: 2 3;
}

.hn-level-line.is-next {
  stroke-width: 1.8;
}

.hn-level-line.is-complete {
  opacity: 0.42;
}

.hn-level-line.is-closed {
  opacity: 0.22;
}

.hn-chart-target-note {
  color: rgba(255, 255, 255, 0.65);
  font-size: 12px;
  line-height: 1.5;
  margin: 8px 0 0;
}

.hn-current-price-line {
  stroke: rgba(255, 255, 255, 0.62);
  stroke-dasharray: 2 4;
}

.hn-current-price-line.is-realtime {
  stroke: var(--hn-accent);
  stroke-dasharray: none;
}

.hn-current-price-dot {
  fill: #fff;
  stroke: #141914;
  stroke-width: 1.5;
}

.hn-current-price-dot.is-realtime {
  fill: var(--hn-accent);
}

.hn-price-axis {
  color: rgba(255, 255, 255, 0.63);
  font-size: 13px;
  font-variant-numeric: tabular-nums;
  inset: 0 0 auto auto;
  pointer-events: none;
  position: absolute;
  width: 68px;
}

.hn-price-axis span {
  position: absolute;
  right: 0;
  transform: translateY(-50%);
  white-space: nowrap;
}

.hn-time-axis {
  color: rgba(255, 255, 255, 0.58);
  display: flex;
  font-size: 13px;
  justify-content: space-between;
  left: 16px;
  pointer-events: none;
  position: absolute;
  right: 76px;
  top: 332px;
}

.hn-level-tags {
  inset: 0 76px 22px 0;
  pointer-events: none;
  position: absolute;
}

.hn-level-tags span {
  background: #20271e;
  border-left: 2px solid var(--hn-accent);
  color: rgba(255, 255, 255, 0.82);
  font-size: 13px;
  font-weight: 700;
  max-width: 160px;
  overflow: hidden;
  padding: 2px 5px;
  position: absolute;
  right: 4px;
  text-overflow: ellipsis;
  transform: translateY(-50%);
  white-space: nowrap;
}

.hn-level-tags span.is-reference {
  border-color: rgba(255, 255, 255, 0.48);
}

.hn-level-tags span.is-entry {
  border-color: #79aef4;
}

.hn-level-tags span.is-entry-high {
  border-color: #c4a0ff;
}

.hn-level-tags span {
  align-items: center;
  display: flex;
  gap: 6px;
}

.hn-level-tags b {
  font-weight: inherit;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}

.hn-level-tags strong {
  flex: 0 0 auto;
  font-variant-numeric: tabular-nums;
  font-weight: inherit;
}

.hn-level-tags span.is-entry {
  max-width: min(260px, 100%);
}

.hn-level-tags span.is-initial {
  border-color: rgba(255, 255, 255, 0.92);
}

.hn-level-tags span.is-add {
  border-color: #79aef4;
}

.hn-level-tags span.is-average {
  border-color: #ffd477;
}

.hn-level-tags span.is-target-2 {
  border-color: #c7de89;
}

.hn-level-tags span.is-target-3 {
  border-color: #e1edc1;
}

.hn-level-tags span.is-order {
  border-color: rgba(255, 255, 255, 0.9);
}

.hn-level-tags span.is-next {
  background: #263120;
  color: #fff;
}

.hn-level-tags span.is-complete {
  opacity: 0.58;
}

.hn-level-tags span.is-closed {
  opacity: 0.36;
}

.hn-current-price-marker {
  background: rgba(255, 255, 255, 0.85);
  border-radius: 4px;
  color: #182015;
  font-size: 13px;
  font-variant-numeric: tabular-nums;
  font-weight: 800;
  line-height: 22px;
  min-width: 66px;
  padding: 0 5px;
  position: absolute;
  right: 0;
  text-align: right;
  transform: translateY(-50%);
  z-index: 1;
}

.hn-current-price-marker.is-realtime {
  background: var(--hn-accent);
}

.hn-candle-tooltip {
  background: rgba(25, 30, 24, 0.96);
  border: 1px solid rgba(255, 255, 255, 0.16);
  border-radius: 10px;
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.3);
  box-sizing: border-box;
  color: #fff;
  padding: 10px 11px;
  pointer-events: none;
  position: absolute;
  right: calc(var(--hn-chart-axis-space) + 10px);
  top: 10px;
  width: min(218px, 100% - var(--hn-chart-axis-space) - 20px);
  z-index: 4;
}

.hn-candle-tooltip.is-near-pointer {
  right: auto;
}

.hn-candle-tooltip header {
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-bottom: 9px;
}

.hn-candle-tooltip time {
  color: rgba(255, 255, 255, 0.72);
  font-size: 13px;
  font-variant-numeric: tabular-nums;
  font-weight: 700;
}

.hn-candle-tooltip em {
  font-size: 13px;
  font-style: normal;
  font-variant-numeric: tabular-nums;
  font-weight: 800;
}

.hn-candle-tooltip em.is-up {
  color: #ff948c;
}

.hn-candle-tooltip em.is-down {
  color: #78abe8;
}

.hn-candle-tooltip dl {
  display: grid;
  gap: 7px 12px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  margin: 0;
}

.hn-candle-tooltip dl > div {
  align-items: baseline;
  display: flex;
  justify-content: space-between;
  min-width: 0;
}

.hn-candle-tooltip dt {
  color: rgba(255, 255, 255, 0.58);
  font-size: var(--hn-text-caption);
}

.hn-candle-tooltip dd {
  font-size: 13px;
  font-variant-numeric: tabular-nums;
  font-weight: 750;
  margin: 0;
  white-space: nowrap;
}

.hn-candle-tooltip footer {
  align-items: center;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  justify-content: space-between;
  margin-top: 9px;
  padding-top: 8px;
}

.hn-candle-tooltip footer span {
  color: rgba(255, 255, 255, 0.6);
  font-size: var(--hn-text-caption);
}

.hn-candle-tooltip footer strong {
  font-size: 13px;
  font-variant-numeric: tabular-nums;
  font-weight: 800;
}

.hn-chart-empty {
  align-items: center;
  color: rgba(255, 255, 255, 0.66);
  display: flex;
  font-size: 13px;
  height: 320px;
  inset: 0 76px auto 0;
  justify-content: center;
  position: absolute;
}

.hn-chart-levels {
  align-items: center;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  display: flex;
  gap: 14px;
  min-height: 34px;
  overflow-x: auto;
  padding: 8px 4px 0;
  scrollbar-width: none;
}

.hn-chart-levels::-webkit-scrollbar {
  display: none;
}

.hn-chart-level {
  align-items: center;
  display: inline-flex;
  flex: 0 0 auto;
  gap: 6px;
  white-space: nowrap;
}

.hn-chart-level i {
  background: var(--hn-accent);
  height: 2px;
  width: 13px;
}

.hn-chart-level.is-reference i {
  background: rgba(255, 255, 255, 0.5);
}

.hn-chart-level.is-entry i {
  background: #79aef4;
}

.hn-chart-level.is-entry-high i {
  background: #c4a0ff;
}

.hn-chart-levels.is-entry-search {
  flex-wrap: wrap;
  gap: 8px 16px;
  overflow-x: visible;
}

.hn-chart-level.is-initial i {
  background: rgba(255, 255, 255, 0.92);
}

.hn-chart-level.is-add i {
  background: #79aef4;
}

.hn-chart-level.is-average i {
  background: #ffd477;
}

.hn-chart-level.is-target-2 i {
  background: #c7de89;
}

.hn-chart-level.is-target-3 i {
  background: #e1edc1;
}

.hn-chart-level.is-order i {
  background: rgba(255, 255, 255, 0.9);
}

.hn-chart-level.is-complete {
  opacity: 0.58;
}

.hn-chart-level.is-closed {
  opacity: 0.4;
}

.hn-chart-level strong {
  font-size: 13px;
}

.hn-chart-level small {
  color: rgba(255, 255, 255, 0.72);
  font-size: 13px;
}

.hn-chart-level em {
  background: rgba(255, 255, 255, 0.08);
  border-radius: 999px;
  color: rgba(255, 255, 255, 0.7);
  font-size: var(--hn-text-caption);
  font-style: normal;
  font-weight: 700;
  padding: 2px 5px;
}

@container (max-width: 680px) {
  .hn-chart-toolbar {
    align-items: stretch;
    flex-direction: column;
    gap: 9px;
  }
  .hn-chart-readout {
    display: grid;
    gap: 0;
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
  .hn-chart-readout > div {
    display: grid;
    gap: 3px;
  }
  .hn-chart-readout > div + div {
    border-left: 1px solid rgba(255, 255, 255, 0.08);
    padding-left: 8px;
  }
  .hn-chart-readout > div:nth-child(5) {
    border-left: 0;
    border-top: 1px solid rgba(255, 255, 255, 0.08);
    display: flex;
    gap: 8px;
    grid-column: 1/-1;
    justify-content: flex-end;
    margin-top: 8px;
    padding: 7px 0 0;
  }
  .hn-chart-readout dt {
    font-size: 13px;
  }
  .hn-chart-readout dd {
    font-size: 13px;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}
@media (max-width: 560px) {
  .hn-chart-shell {
    padding: 9px 10px 11px;
  }
  .hn-chart-toolbar {
    padding: 0 2px 8px;
  }
  .hn-chart-context span,
  .hn-chart-context time {
    font-size: 13px;
  }
  .hn-chart-plot {
    --hn-chart-axis-space: 70px;
    padding-right: 70px;
  }
  .hn-price-axis {
    width: 64px;
  }
  .hn-time-axis {
    right: 70px;
  }
  .hn-level-tags {
    right: 70px;
  }
  .hn-level-tags span:not(.is-entry) {
    display: none;
  }
  .hn-chart-levels {
    gap: 15px;
    padding-left: 3px;
    padding-right: 3px;
  }
}
@media (max-width: 380px) {
  .hn-chart-context {
    gap: 7px;
  }
  .hn-chart-context time::before {
    margin-right: 7px;
  }
  .hn-chart-readout > div + div {
    padding-left: 5px;
  }
  .hn-chart-readout dd {
    font-size: 13px;
  }
}`],
})
export class ComponentMarketChartComponent implements OnChanges, OnDestroy {
    @Input() bars: any[] = [];
    @Input() cycle: any = null;
    @Input() order: any = null;
    @Input() market: any = {};
    @Input() strategy: any = null;
    @Input() session = 'CALENDAR_UNKNOWN';
    @Input() hasTarget = false;

    public model: ChartModel = buildChartModel([], null, null, {});
    public selection: ChartSelection | null = null;
    public interactionMode: InteractionMode = null;
    public tooltipPosition: TooltipPosition | null = null;
    public pointerFocused = false;
    public readonly formatBarTime = formatBarTime;
    public readonly formatNumber = formatNumber;
    public readonly formatPercent = formatPercent;
    public readonly formatPrice = formatPrice;
    public readonly formatTime = formatTime;
    public readonly formatVolume = formatVolume;
    private renderFrame: number | null = null;

    constructor(@Inject( Service) public service: Service) { }

    public ngOnDestroy() {
        if (this.renderFrame !== null && typeof cancelAnimationFrame === 'function') {
            cancelAnimationFrame(this.renderFrame);
        }
        this.renderFrame = null;
    }

    private renderInteraction() {
        if (this.renderFrame !== null) return;
        if (typeof requestAnimationFrame !== 'function') {
            void this.service.render();
            return;
        }
        this.renderFrame = requestAnimationFrame(() => {
            this.renderFrame = null;
            void this.service.render();
        });
    }

    public ngOnChanges(_changes: SimpleChanges) {
        const selectedKey = this.selection?.candle.key || '';
        const viewportWidth = typeof window === 'undefined'
            ? Number.POSITIVE_INFINITY : window.innerWidth;
        const visibleBars = Array.isArray(this.bars)
            ? this.bars.slice(-chartBarLimit(viewportWidth)) : [];
        this.model = buildChartModel(
            visibleBars, this.cycle, this.order, this.market, this.strategy
        );
        this.selection = selectedKey
            ? selectionByKey(this.model, selectedKey)
            : null;
        if (!this.selection) {
            this.interactionMode = null;
            this.tooltipPosition = null;
        }
    }

    public get displayBar(): MarketBar | null {
        return this.selection?.candle || this.model.latest;
    }

    public get displayIndex() {
        return this.selection?.index ?? Math.max(0, this.model.candles.length - 1);
    }

    public get displayChangePct(): number | null {
        return displayChangePercentage(this.selection, this.market, this.displayBar);
    }

    public get readoutLabel() {
        return this.selection ? '선택한 확정 1분봉' : '최근 확정 1분봉';
    }

    public get ariaValueText() {
        return chartAriaValueText(this.displayBar, this.displayChangePct)
            || this.emptyText;
    }

    public trackByCandle(_index: number, bar: MarketBar) {
        return bar.key;
    }

    public trackByLevel(_index: number, level: ChartLevel) {
        return level.key;
    }

    public trackByTick(_index: number, tick: PriceTick) {
        return tick.key;
    }

    public levelStateLabel(level: ChartLevel) {
        if (level.kind === 'entry' || level.kind === 'entry-high') return '';
        if (level.status === 'active') return '주문 중';
        if (level.status === 'next') return '다음';
        if (level.status === 'complete') return '완료';
        if (level.status === 'closed') return '종료';
        return '';
    }

    private select(
        selection: ChartSelection | null,
        mode: Exclude<InteractionMode, null>
    ) {
        if (!selection) {
            if (mode === 'mouse') this.clearSelection();
            return;
        }
        const changed = selection.candle.key !== this.selection?.candle.key;
        if (!changed && mode === this.interactionMode) return;
        this.selection = selection;
        this.interactionMode = mode;
    }

    public onPointerMove(event: PointerEvent) {
        if (!event.isPrimary || event.pointerType === 'touch') return;
        const context = pointerContext(this.model, event);
        if (!context) {
            this.clearSelection();
            return;
        }
        this.tooltipPosition = context.tooltipPosition;
        this.select(context.selection, 'mouse');
        this.renderInteraction();
    }

    public onPointerDown(event: PointerEvent) {
        if (!event.isPrimary) return;
        this.pointerFocused = true;
        this.renderInteraction();
    }

    public onPointerUp(event: PointerEvent) {
        if (!event.isPrimary || event.pointerType !== 'touch') return;
        const context = pointerContext(this.model, event);
        if (!context) return;
        this.tooltipPosition = context.tooltipPosition;
        this.select(context.selection, 'touch');
        this.renderInteraction();
    }

    public onPointerLeave(event: PointerEvent) {
        if (event.pointerType !== 'touch' && this.interactionMode === 'mouse') {
            this.clearSelection();
        }
    }

    public onPointerCancel(event: PointerEvent) {
        if (event.pointerType !== 'touch') this.clearSelection();
    }

    public onChartKeydown(event: KeyboardEvent) {
        this.pointerFocused = false;
        if (!this.model.candles.length) return;
        const last = this.model.candles.length - 1;
        const current = this.selection?.index ?? last;
        const indexes: Record<string, number> = {
            ArrowLeft: current - 1,
            ArrowRight: current + 1,
            Home: 0,
            End: last
        };
        if (event.key === 'Escape') {
            event.preventDefault();
            this.clearSelection();
            return;
        }
        if (!(event.key in indexes)) return;
        event.preventDefault();
        this.tooltipPosition = null;
        this.select(
            selectionAtIndex(this.model, indexes[event.key]),
            'keyboard'
        );
        this.renderInteraction();
    }

    public onChartBlur() {
        this.pointerFocused = false;
        if (this.interactionMode === 'keyboard') this.clearSelection();
        this.renderInteraction();
    }

    public clearSelection() {
        this.selection = null;
        this.interactionMode = null;
        this.tooltipPosition = null;
        this.renderInteraction();
    }

    public get emptyText() {
        return emptyChartText(this.hasTarget, this.session, this.market);
    }

    public get summary() {
        return chartSummary(this.model, this.selection, this.market, this.emptyText);
    }

}

export default ComponentMarketChartComponent;